import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Tent } from 'lucide-react';
import type { CampingSpot } from '../../types';
import ExtendStayControl from './ExtendStayControl';

const AUTO_DISMISS_MS = 20000;
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const DISMISS_STORAGE_KEY = 'autoCheckInDismissed_v2';

interface AutoCheckInBannerProps {
  spot: CampingSpot;
  /** `checkin` = tilby innsjekk (standard). `extend` = brukeren står her alt → tilby forlengelse. */
  mode?: 'checkin' | 'extend';
  onCheckIn: (spotId: string) => void;
  onExtend?: (spotId: string, days: number) => void;
  onDismiss: () => void;
}

const AutoCheckInBanner: React.FC<AutoCheckInBannerProps> = ({ spot, mode = 'checkin', onCheckIn, onExtend, onDismiss }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(true);
  const [fading, setFading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    // Ikke auto-lukk forleng-varselet — brukeren kan bruke tid på å rulle antall netter.
    if (mode === 'extend') return;
    timerRef.current = setTimeout(() => {
      setFading(true);
      setTimeout(() => {
        setVisible(false);
        onDismiss();
      }, 300);
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(timerRef.current);
  }, [onDismiss]);

  const handleCheckIn = () => {
    clearTimeout(timerRef.current);
    onCheckIn(spot.id);
    setVisible(false);
  };

  const handleClose = () => {
    clearTimeout(timerRef.current);
    setFading(true);
    setTimeout(() => {
      setVisible(false);
      onDismiss();
    }, 200);
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed bottom-[calc(env(safe-area-inset-bottom,0px)+5rem)] left-4 right-4 z-[9998] transition-all duration-300 ${
        fading ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0 animate-in slide-in-from-bottom-4'
      }`}
    >
      <div className="bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-2xl shadow-2xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
          <Tent size={20} className="text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-bold text-[var(--text-primary)] truncate">
            {mode === 'extend'
              ? t('spot.occupied.extend_prompt', { name: spot.name, defaultValue: 'Blir du værende på {{name}}?' })
              : t('spot.occupied.check_in_prompt', { name: spot.name })}
          </p>
          {spot.spotType === 'rasteplass' && (
            <p className="text-3xs text-[var(--text-muted)] mt-0.5">
              {t('spot.occupied.privacy_note', 'Andre ser kun at noen bruker plassen, ikke hvem.')}
            </p>
          )}
          {/* Countdown bar — kun i innsjekk-modus (forleng auto-lukkes ikke) */}
          {mode !== 'extend' && (
            <div className="mt-1.5 h-1 rounded-full bg-[var(--border-primary)] overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full"
                style={{
                  animation: `shrink ${AUTO_DISMISS_MS}ms linear forwards`,
                }}
              />
            </div>
          )}
        </div>
        {mode === 'extend' && onExtend ? (
          <div className="shrink-0 min-w-[9.5rem] max-w-[12rem]">
            <ExtendStayControl
              variant="banner"
              onExtend={(days) => { clearTimeout(timerRef.current); onExtend(spot.id, days); setVisible(false); }}
            />
          </div>
        ) : (
          <button
            onClick={handleCheckIn}
            className="px-4 py-2 rounded-xl bg-emerald-500 text-white text-tiny font-black uppercase tracking-wider shrink-0 active:scale-95 transition-transform"
          >
            {t('spot.occupied.check_in')}
          </button>
        )}
        <button
          onClick={handleClose}
          className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] shrink-0"
        >
          <X size={16} />
        </button>
      </div>
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default AutoCheckInBanner;

// --- Utility: check/set dismiss cooldown (per-spot, 24h) ---
function readDismissMap(): Record<string, number> {
  try {
    const stored = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch { return {}; }
}

function writeDismissMap(map: Record<string, number>): void {
  try { localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(map)); } catch {}
}

export function isDismissedRecently(spotId: string): boolean {
  const map = readDismissMap();
  const ts = map[spotId];
  return typeof ts === 'number' && Date.now() - ts < DISMISS_COOLDOWN_MS;
}

export function setDismissed(spotId: string): void {
  const map = readDismissMap();
  const now = Date.now();
  // Drop entries older than the cooldown window so the map doesn't grow forever
  for (const id of Object.keys(map)) {
    if (now - map[id] >= DISMISS_COOLDOWN_MS) delete map[id];
  }
  map[spotId] = now;
  writeDismissMap(map);
}
